<template>
  <base-drawer
    class="calendar-settings-drawer"
    :show="show"
    :title="policyOnly ? 'View settings' : 'Calendar settings'"
    @close="onClose"
  >
    <!-- Loading skeleton -->
    <template v-if="loadingSettings">
      <div class="skeleton-section mb-3">
        <div class="skel" style="width: 40%; height: 14px; margin-bottom: 16px;"></div>
        <div class="skel" style="width: 100%; height: 36px; margin-bottom: 12px;"></div>
        <div class="skel" style="width: 100%; height: 36px; margin-bottom: 12px;"></div>
        <div class="skel" style="width: 60%; height: 36px;"></div>
      </div>
      <div class="skeleton-section">
        <div class="skel" style="width: 35%; height: 14px; margin-bottom: 16px;"></div>
        <div class="skel" style="width: 100%; height: 36px; margin-bottom: 12px;"></div>
        <div class="skel" style="width: 100%; height: 36px;"></div>
      </div>
    </template>

    <!-- Body -->
    <template v-else>

          <!-- Section 1: Column and layout -->
          <drawer-section v-if="!policyOnly" title="Column and layout" icon="icon-components">
            <template v-if="canEditCalendarPolicy" #header-actions>
              <span class="scope-tag scope-tag--personal">Personal</span>
            </template>

            <!-- Min column widths only apply to the main calendar view -->
            <template v-if="viewType === 'calendar'">
              <div class="toggle-row mb-2">
                <el-switch
                  active-color="#e14eca"
                  v-model="form.enable_min_col_width_desktop"
                />
                <label
                  class="mb-0 ml-2"
                  style="cursor: pointer"
                  @click="form.enable_min_col_width_desktop = !form.enable_min_col_width_desktop"
                >
                  Minimum column width on desktop
                  <el-popover trigger="hover" placement="right">
                    <div>
                      <div class="popover-body">Resource columns in the calendar will maintain a minimum width, <br/>with horizontal overflow handled via scrolling.</div>
                    </div>
                    <i slot="reference" style="color: #1d8cf8" class="tim-icons el-icon-info"></i>
                  </el-popover>
                </label>
              </div>
              <div v-if="form.enable_min_col_width_desktop" class="conditional-field mb-3">
                <base-input label="Minimum column width">
                  <el-input-number
                    :controls="false"
                    v-model="form.min_width_desktop"
                    :min="50"
                    :max="500"
                  />
                </base-input>
              </div>

              <div class="toggle-row mb-2">
                <el-switch
                  active-color="#e14eca"
                  v-model="form.enable_min_col_width_mobile"
                />
                <label
                  class="mb-0 ml-2"
                  style="cursor: pointer"
                  @click="form.enable_min_col_width_mobile = !form.enable_min_col_width_mobile"
                >
                  Minimum column width on mobile
                  <el-popover trigger="hover" placement="right">
                    <div>
                      <div class="popover-body">Resource columns in the calendar will maintain a minimum width, <br/>with horizontal overflow handled via scrolling.</div>
                    </div>
                    <i slot="reference" style="color: #1d8cf8" class="tim-icons el-icon-info"></i>
                  </el-popover>
                </label>
              </div>
              <div v-if="form.enable_min_col_width_mobile" class="conditional-field mb-3">
                <base-input label="Minimum column width">
                  <el-input-number
                    :controls="false"
                    v-model="form.min_width_mobile"
                    :min="50"
                    :max="500"
                  />
                </base-input>
              </div>

              <div class="section-divider"></div>
            </template>

            <!-- Slot height (both views) -->
            <div class="mt-3">
              <base-input>
                <label slot="label">Slot height (20-200 pixels)
                  <el-popover trigger="hover" placement="right">
                    <div>
                      <div class="popover-body">
                        Increase this value to show more booking information <br/>in smaller time slots.
                      </div>
                    </div>
                    <i slot="reference" style="color: #1d8cf8" class="tim-icons el-icon-info"></i>
                  </el-popover>
                </label>
                <el-input-number
                  :controls="false"
                  v-model="form.slot_height"
                  :min="20"
                  :max="200"
                  placeholder="20"
                />
              </base-input>
            </div>

            <!-- Default column sort order (resource calendar only) -->
            <div v-if="viewType === 'resource-calendar'" class="mt-3">
              <base-input label="Default column sort order">
                <el-select v-model="form.resource_sort_method" style="width: 100%;">
                  <el-option
                    v-for="opt in sortOptions"
                    :key="opt.value"
                    :value="opt.value"
                    :label="opt.label"
                  />
                </el-select>
              </base-input>
            </div>
          </drawer-section>

          <!-- Section 2: Time slot behavior. Holds both the per-user display
               toggle and the venue-wide cutoff indicators, so scope tags sit on
               the rows rather than the section header. -->
          <drawer-section title="Time slot behavior" icon="icon-calendar-60">
            <div v-if="!policyOnly" class="toggle-row">
              <el-switch
                active-color="#e14eca"
                v-model="form.show_schedule_name"
              />
              <label
                class="mb-0 ml-2"
                style="cursor: pointer"
                @click="form.show_schedule_name = !form.show_schedule_name"
              >
                Show schedule name on time slot
                <el-popover trigger="hover" placement="right">
                  <div>
                    <div class="popover-body">Calendar time slots will display a label showing <br/>which schedule was used to generate the slot.</div>
                  </div>
                  <i slot="reference" style="color: #1d8cf8" class="tim-icons el-icon-info"></i>
                </el-popover>
              </label>
              <span v-if="canEditCalendarPolicy" class="scope-tag scope-tag--personal">Personal</span>
            </div>
            <div v-if="!policyOnly" class="toggle-row mt-3">
              <el-switch
                active-color="#e14eca"
                v-model="form.hide_blocked_overlapping_bookings"
              />
              <label
                class="mb-0 ml-2"
                style="cursor: pointer"
                @click="form.hide_blocked_overlapping_bookings = !form.hide_blocked_overlapping_bookings"
              >
                Hide blocked slots that overlap a booking
                <el-popover trigger="hover" placement="right">
                  <div>
                    <div class="popover-body">Blocked and call to book slots whose time overlaps a booked slot<br/>for the same event are left off the calendar, so the booking<br/>is not covered by the slots that were blocked around it.</div>
                  </div>
                  <i slot="reference" style="color: #1d8cf8" class="tim-icons el-icon-info"></i>
                </el-popover>
              </label>
              <span v-if="canEditCalendarPolicy" class="scope-tag scope-tag--personal">Personal</span>
            </div>

            <div v-if="canEditCalendarPolicy" class="toggle-row" :class="{ 'mt-3': !policyOnly }">
              <el-switch
                active-color="#e14eca"
                v-model="policy.show_notice_indicators"
              />
              <label
                class="mb-0 ml-2"
                style="cursor: pointer"
                @click="policy.show_notice_indicators = !policy.show_notice_indicators"
              >
                Show online booking cutoff on time slots
                <el-popover trigger="hover" placement="right">
                  <div>
                    <div class="popover-body">Available time slots that customers can no longer book online <br/>show a label with what customers see instead: <br/>call to book, request to book, or hidden.</div>
                  </div>
                  <i slot="reference" style="color: #1d8cf8" class="tim-icons el-icon-info"></i>
                </el-popover>
              </label>
              <span class="scope-tag scope-tag--venue">Venue-wide</span>
            </div>

            <div v-if="canEditCalendarPolicy && policy.show_notice_indicators" class="mt-3">
              <label class="snap-label">Cutoff display
                <el-popover trigger="hover" placement="right">
                  <div>
                    <div class="popover-body">Label and countdown also shows how long until online booking <br/>closes, starting two hours before the cutoff.</div>
                  </div>
                  <i slot="reference" style="color: #1d8cf8" class="tim-icons el-icon-info"></i>
                </el-popover>
              </label>
              <segmented-toggle
                v-model="policy.notice_display_mode"
                :options="noticeDisplayOptions"
                size="sm"
              />
            </div>
          </drawer-section>

          <!-- Section 3: Calendar behavior (per-group policy, gated by permission).
               Drag rules apply to the calendar views only, so this section is
               hidden in policy-only mode (bookings list, kanban). -->
          <drawer-section v-if="canEditCalendarPolicy && !policyOnly" title="Calendar behavior" icon="icon-settings">
            <template #header-actions>
              <span class="scope-tag scope-tag--venue">Venue-wide</span>
            </template>
            <div class="toggle-row mb-2">
              <el-switch
                active-color="#e14eca"
                v-model="policy.drag_enabled_desktop"
              />
              <label
                class="mb-0 ml-2"
                style="cursor: pointer"
                @click="policy.drag_enabled_desktop = !policy.drag_enabled_desktop"
              >
                Allow drag and drop on desktop
                <el-popover trigger="hover" placement="right">
                  <div>
                    <div class="popover-body">Staff with the booking-slot edit permission can drag <br/>and resize slots on the calendar from a desktop browser.</div>
                  </div>
                  <i slot="reference" style="color: #1d8cf8" class="tim-icons el-icon-info"></i>
                </el-popover>
              </label>
            </div>

            <div class="toggle-row mb-2">
              <el-switch
                active-color="#e14eca"
                v-model="policy.drag_enabled_mobile"
              />
              <label
                class="mb-0 ml-2"
                style="cursor: pointer"
                @click="policy.drag_enabled_mobile = !policy.drag_enabled_mobile"
              >
                Allow drag and drop on mobile
                <el-popover trigger="hover" placement="right">
                  <div>
                    <div class="popover-body">Off by default. Mobile drags are easy to trigger by accident, <br/>so most venues keep this disabled even when staff have edit access.</div>
                  </div>
                  <i slot="reference" style="color: #1d8cf8" class="tim-icons el-icon-info"></i>
                </el-popover>
              </label>
            </div>

            <div v-if="policy.drag_enabled_desktop || policy.drag_enabled_mobile" class="mt-3 mb-3">
              <label class="snap-label">Snap interval</label>
              <segmented-toggle
                v-model="policy.drag_snap_minutes"
                :options="snapOptions"
                size="sm"
              />
            </div>

            <div v-if="policy.drag_enabled_desktop || policy.drag_enabled_mobile" class="toggle-row">
              <el-switch
                active-color="#e14eca"
                v-model="policy.confirm_drag"
              />
              <label
                class="mb-0 ml-2"
                style="cursor: pointer"
                @click="policy.confirm_drag = !policy.confirm_drag"
              >
                Always confirm before moving a slot
                <el-popover trigger="hover" placement="right">
                  <div>
                    <div class="popover-body">When on, every drag or resize prompts for confirmation before saving. <br/>When off, only booked slots prompt.</div>
                  </div>
                  <i slot="reference" style="color: #1d8cf8" class="tim-icons el-icon-info"></i>
                </el-popover>
              </label>
            </div>

          </drawer-section>
    </template>

    <!-- Footer -->
    <template #footer>
      <div class="footer-actions">
        <base-button type="secondary" size="sm" @click="onClose">Cancel</base-button>
        <base-button type="primary" size="sm" :disabled="saving" @click="saveSettings">
          <i v-if="saving" class="el-icon-loading mr-1"></i>Save
        </base-button>
      </div>
    </template>
  </base-drawer>
</template>

<script>
import { API_LOCATION } from '@/api-config';
import axios from 'axios';
import { Auth } from 'aws-amplify';
import swal from 'sweetalert2';
import {
  Popover,
  Switch,
  InputNumber,
  Select,
  Option
} from 'element-ui';

export default {
  name: 'CalendarSettingsDrawer',

  components: {
    [Popover.name]: Popover,
    [Switch.name]: Switch,
    [InputNumber.name]: InputNumber,
    [Select.name]: Select,
    [Option.name]: Option
  },

  props: {
    show: { type: Boolean, default: false },
    companyGroupId: { type: [String, Number], required: true },
    viewType: {
      type: String,
      default: 'calendar',
      validator: v => ['calendar', 'resource-calendar'].includes(v)
    },
    // Venue-wide section only. Used by views without calendar display
    // settings (bookings list, kanban) so the group policy, including the
    // notice indicator settings, is reachable from every booking view.
    policyOnly: { type: Boolean, default: false }
  },

  data() {
    return {
      loadingSettings: false,
      saving: false,
      settingsId: null,
      canEditCalendarPolicy: false,
      form: {
        enable_min_col_width_desktop: false,
        min_width_desktop: 250,
        enable_min_col_width_mobile: false,
        min_width_mobile: 250,
        slot_height: 20,
        show_schedule_name: false,
        hide_blocked_overlapping_bookings: false,
        resource_sort_method: 'alphabetical'
      },
      sortOptions: [
        { value: 'alphabetical', label: 'Alphabetical' },
        { value: 'earliest_start_time', label: 'Earliest start time' },
        { value: 'latest_start_time', label: 'Latest start time' }
      ],
      noticeDisplayOptions: [
        { value: 'indicator', label: 'Label only' },
        { value: 'countdown', label: 'Label and countdown' }
      ],
      policy: {
        drag_enabled_desktop: true,
        drag_enabled_mobile: false,
        drag_snap_minutes: 5,
        confirm_drag: false,
        show_notice_indicators: false,
        notice_display_mode: 'indicator'
      },
      initialForm: null,
      initialPolicy: null,
      snapOptions: [
        { value: 5, label: '5 min' },
        { value: 15, label: '15 min' }
      ]
    };
  },

  computed: {
    userDirty() {
      if (!this.initialForm) return false;
      return JSON.stringify(this.form) !== JSON.stringify(this.initialForm);
    },
    policyDirty() {
      if (!this.initialPolicy) return false;
      return JSON.stringify(this.policy) !== JSON.stringify(this.initialPolicy);
    }
  },

  watch: {
    show(newVal) {
      if (newVal) {
        document.addEventListener('keydown', this._onEscKey);
        this.loadSettings();
      } else {
        document.removeEventListener('keydown', this._onEscKey);
      }
    }
  },

  created() {
    this._onEscKey = (e) => {
      if (e.key === 'Escape') this.onClose();
    };
  },

  beforeDestroy() {
    document.removeEventListener('keydown', this._onEscKey);
  },

  methods: {
    async loadSettings() {
      this.loadingSettings = true;
      try {
        const session = await Auth.currentSession();
        const token = session.getIdToken().getJwtToken();
        const url = `${API_LOCATION}users/site-settings?group=${this.companyGroupId}&view_type=${this.viewType}`;
        const config = { headers: { Authorization: token } };

        const response = await axios.get(url, config);
        const displaySettings = response.data.calendar_display_settings;
        const policySettings = response.data.calendar_policy_settings;
        this.canEditCalendarPolicy = Boolean(response.data.can_edit_calendar_policy);

        if (displaySettings) {
          this.settingsId = displaySettings.id;
          this.form.enable_min_col_width_desktop = Boolean(displaySettings.enable_min_col_width_desktop);
          this.form.enable_min_col_width_mobile = Boolean(displaySettings.enable_min_col_width_mobile);
          this.form.min_width_desktop = displaySettings.min_width_desktop || 250;
          this.form.min_width_mobile = displaySettings.min_width_mobile || 250;
          this.form.show_schedule_name = Boolean(displaySettings.show_schedule_name);
          this.form.hide_blocked_overlapping_bookings = Boolean(displaySettings.hide_blocked_overlapping_bookings);
          this.form.slot_height = displaySettings.slot_height || 20;
          this.form.resource_sort_method = displaySettings.resource_sort_method || 'alphabetical';
        }

        if (policySettings) {
          this.policy.drag_enabled_desktop = Boolean(policySettings.drag_enabled_desktop);
          this.policy.drag_enabled_mobile = Boolean(policySettings.drag_enabled_mobile);
          this.policy.drag_snap_minutes = policySettings.drag_snap_minutes || 5;
          this.policy.confirm_drag = Boolean(policySettings.confirm_drag);
          this.policy.show_notice_indicators = Boolean(policySettings.show_notice_indicators);
          this.policy.notice_display_mode = policySettings.notice_display_mode || 'indicator';
        }

        // Snapshot for dirty tracking.
        this.initialForm = JSON.parse(JSON.stringify(this.form));
        this.initialPolicy = JSON.parse(JSON.stringify(this.policy));
      } catch (err) {
        console.log(err);
      } finally {
        this.loadingSettings = false;
      }
    },

    async saveSettings() {
      // Nothing changed
      if (!this.userDirty && !this.policyDirty) {
        this.$emit('update:show', false);
        this.$emit('close');
        return;
      }

      this.saving = true;
      try {
        const session = await Auth.currentSession();
        const token = session.getIdToken().getJwtToken();
        const url = `${API_LOCATION}users/site-settings`;
        const config = { headers: { Authorization: token } };

        // Build a single payload with whatever's dirty. The backend detects
        // which paths to update based on which fields are present.
        const data = {};

        if (this.userDirty) {
          data.id = this.settingsId;
          data.view_type = this.viewType;
          data.enable_min_col_width_desktop = this.form.enable_min_col_width_desktop;
          data.enable_min_col_width_mobile = this.form.enable_min_col_width_mobile;
          data.min_width_desktop = this.form.min_width_desktop;
          data.min_width_mobile = this.form.min_width_mobile;
          data.show_schedule_name = this.form.show_schedule_name;
          data.hide_blocked_overlapping_bookings = this.form.hide_blocked_overlapping_bookings;
          data.slot_height = this.form.slot_height;
          data.resource_sort_method = this.form.resource_sort_method;
        }

        if (this.policyDirty && this.canEditCalendarPolicy) {
          data.company_group_id = this.companyGroupId;
          data.drag_enabled_desktop = this.policy.drag_enabled_desktop;
          data.drag_enabled_mobile = this.policy.drag_enabled_mobile;
          data.drag_snap_minutes = this.policy.drag_snap_minutes;
          data.confirm_drag = this.policy.confirm_drag;
          data.show_notice_indicators = this.policy.show_notice_indicators;
          data.notice_display_mode = this.policy.notice_display_mode;
        }

        await axios.put(url, data, config);

        this.$notify({ type: 'success', message: 'Settings saved', timeout: 3000 });
        this.$emit('saved', { form: { ...this.form }, policy: { ...this.policy } });
        this.$emit('update:show', false);
        this.$emit('close');
      } catch (err) {
        console.log(err);
        swal('Error!', 'Something went wrong', 'error');
      } finally {
        this.saving = false;
      }
    },

    onClose() {
      this.$emit('update:show', false);
      this.$emit('close');
    }
  }
};
</script>

<style lang="scss" scoped>
.footer-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.section-divider {
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  margin: 4px 0 0 0;
}

// .toggle-row itself comes from the global primitive
// (sass/dashboard/custom/_toggle-row.scss), which carries the responsive
// scaling. Only the gap for this drawer's right-aligned scope tag is local.
.toggle-row > label {
  padding-right: 8px;
}

.conditional-field {
  margin-left: 52px;
  padding-left: 12px;
  border-left: 2px solid rgba(225, 78, 202, 0.4);
  max-width: 200px;
}

.snap-label {
  display: block;
  color: rgba(255, 255, 255, 0.8);
  font-size: 13px;
  margin-bottom: 6px;

  @media (max-width: 768px) {
    font-size: 12px;
  }

  @media (max-width: 480px) {
    font-size: 11px;
  }
}

// Small pill in section headers indicating whether settings are per-user
// ('Personal') or per-group ('Venue-wide'). Only shown to users who can see
// both scopes (admins / calendar_policy_configure).
.scope-tag {
  display: inline-block;
  vertical-align: middle;
  position: relative;
  top: -1px;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 10px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  line-height: 1.4;
  white-space: nowrap;

  // Inside a toggle row the tag is metadata, not part of the label sentence.
  // Pushing it to the right edge keeps it on the row's first line instead of
  // wrapping under the label like a trailing word.
  .toggle-row & {
    margin-left: auto;
    flex-shrink: 0;
    align-self: center;
    top: 0;
  }

  @media (max-width: 768px) {
    padding: 1px 6px;
    font-size: 9px;
    letter-spacing: 0.02em;
  }

  @media (max-width: 480px) {
    padding: 1px 5px;
    font-size: 8px;
    letter-spacing: 0;
  }
}

.scope-tag--personal {
  color: rgba(255, 255, 255, 0.6);
  background: rgba(125, 125, 125, 0.15);
  border: 1px solid rgba(125, 125, 125, 0.25);
}

.scope-tag--venue {
  color: #e14eca;
  background: rgba(225, 78, 202, 0.1);
  border: 1px solid rgba(225, 78, 202, 0.3);
}

.skeleton-section {
  padding: 16px;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
}
</style>

<style lang="scss">
.calendar-settings-drawer {
  .el-input-number {
    width: 100%;
  }
  .el-input-number .el-input__inner {
    text-align: left;
    color: white;
    background: transparent;
    border-color: rgba(255, 255, 255, 0.2);
  }
}

.white-content .calendar-settings-drawer {
  .section-divider {
    border-top-color: #e9ecef;
  }

  .toggle-row label {
    color: #525f7f;
  }

  .conditional-field {
    border-left-color: rgba(225, 78, 202, 0.3);
  }

  .el-input-number .el-input__inner {
    color: #344675;
    border-color: #d1d5db;
  }

  .skeleton-section {
    background: #f8f9fa;
    border-color: #e3e3e3;
  }

  .snap-label {
    color: #525f7f;
  }

  .scope-tag--personal {
    color: rgba(0, 0, 0, 0.5);
    background: rgba(125, 125, 125, 0.1);
    border-color: rgba(125, 125, 125, 0.2);
  }

  .scope-tag--venue {
    color: #e14eca;
    background: rgba(225, 78, 202, 0.06);
    border-color: rgba(225, 78, 202, 0.25);
  }
}
</style>
